import { Button, Center, ChakraProps, HStack, Text } from '@chakra-ui/react';
import { FC, useMemo } from 'react';
import { Icon } from '@app/components/Icon';
import styles from './BidNowButton.module.scss';
import cn from 'clsx';
import { useNftMarketplaceFiltersState } from '@app/state/nftMarketplaceFiltersStore';
import { MakeOfferDialog } from '@app/screens/nft-marketplace/MakeOfferDialog';
import { useToggle } from 'react-use';
import { Token } from '@app/types/token';
import { NFTMarketplaceListing } from '@app/types/nftMarketplace';
import { useAddress } from '@thirdweb-dev/react';
import { useIsBidder } from '@app/hooks/nft/useIsBidder';
import { FaCheck } from 'react-icons/fa6';
import { getToken } from '@app/helpers/token';

type Props = {
  classes?: {
    root?: string;
    bidNowButton?: string;
    icon?: string;
  };
  listing: NFTMarketplaceListing;
  sellCurrency: Token | undefined;
  variant?: 'rowCompact' | 'secondary' | 'primary';
  activeSxProps?: ChakraProps;
} & ChakraProps;

export const BidNowButton: FC<Props> = ({
  classes,
  sellCurrency,
  listing,
  variant,
  activeSxProps = {},
  ...rest
}) => {
  const address = useAddress();
  const [open, toggleOpen] = useToggle(false);
  const { isEligibleToParticipate, isEligibleToParticipateInPremium } =
    useNftMarketplaceFiltersState();
  const { data: isBidder } = useIsBidder(listing.id);

  const isLive = useMemo(() => {
    if (!listing || !listing.isNFTAvailable) {
      return false;
    }

    if (listing.nftMetadata.isPremium && !isEligibleToParticipateInPremium) {
      return false;
    }

    if (!listing.start && !listing.end) {
      return true;
    }

    const now = Date.now();

    return now >= listing.start * 1000 && now < listing.end * 1000;
  }, [listing, isEligibleToParticipateInPremium]);

  const isEnded = useMemo(() => {
    if (!listing || !listing.isNFTAvailable) {
      return true;
    }

    if (!listing.start && !listing.end) {
      return false;
    }

    const now = Date.now();

    return now >= listing.end * 1000;
  }, [listing]);

  if (
    address?.toLowerCase() === listing.maker?.toLowerCase() ||
    !listing.isNFTAvailable ||
    isEnded
  ) {
    return null;
  }

  return (
    <HStack className={cn(styles.root, classes?.root)} gap={0}>
      {isBidder ? (
        <Center
          sx={{
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'success',
            color: 'neutral.300',
            padding: '8px 16px',
            width: '100%',
            background: 'neutral.800',
            ...activeSxProps
          }}
        >
          <FaCheck color="var(--chakra-colors-success" size={18} />
          <Text fontSize={14} color="success" ml={2}>
            Offer made
          </Text>
        </Center>
      ) : (
        <Button
          variant={variant}
          width="100%"
          className={cn(styles.bidNowButton, classes?.bidNowButton)}
          isDisabled={!isEligibleToParticipate || !isLive}
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            toggleOpen(true);
          }}
          {...rest}
        >
          Bid Now
          <Icon name="clock" className={cn(styles.icon)} />
        </Button>
      )}

      {open && (
        <MakeOfferDialog
          isOpen
          onClose={() => toggleOpen(false)}
          token0={listing.token0 && getToken(listing.token0)}
          token1={listing.token1 && getToken(listing.token1)}
          sellCurrency={sellCurrency}
          listing={listing}
          tokenId={listing.tokenId}
          lpTokenId={listing.nftPool?.lpToken.id}
          poolAddress={listing.nftContractAddress}
        />
      )}
    </HStack>
  );
};
